/**
 * Major leaderboard standings — the one place a major's finishing order is
 * built on the Rounds page. The leaderboard tables render these rows, and the
 * Analysis view takes its default (tournament-rank) order from them, so the two
 * can never disagree about who finished where.
 */

export type Metric = 'net' | 'gross';

// Standard competition ranking ("1224"): players who share a total share a rank,
// and the next distinct total skips ahead by however many players tied above it
// — e.g. three players tied for 1st are all 1st and the next player is 4th; two
// tied for 1st makes the next player 3rd. Expects rows already sorted by total
// ascending (a lower golf total is better).
export function withCompetitionRanks<T extends { total: number }>(rows: T[]): (T & { rank: number })[] {
  let rank = 0;
  let prevTotal: number | null = null;
  return rows.map((row, i) => {
    if (prevTotal === null || row.total !== prevTotal) {
      rank = i + 1;
      prevTotal = row.total;
    }
    return { ...row, rank };
  });
}

// ── Scheduled majors (per-player schedule from /rounds-context) ─────────────

export interface ScheduledEntry {
  player_id: number;
  name: string;
  /** The player's released rounds in personal-round order; null = not played yet. */
  scores: ({ gross: number; net: number } | null)[];
}

export interface ScheduledRow {
  player_id: number;
  name: string;
  scores: (number | null)[];
  total: number;
  rank: number;
}

/** Everyone with a released score, ranked by their total over the released rounds. */
export function buildScheduledStandings(entries: ScheduledEntry[], metric: Metric): ScheduledRow[] {
  return withCompetitionRanks(
    entries
      .map((e) => {
        const scores = e.scores.map((s) => (s ? (metric === 'gross' ? s.gross : s.net) : null));
        return {
          player_id: e.player_id,
          name: e.name,
          scores,
          total: scores.reduce<number>((acc, v) => acc + (v ?? 0), 0),
        };
      })
      .filter((p) => p.scores.some((v) => v !== null))
      .sort((a, b) => a.total - b.total),
  );
}

// ── Legacy majors (no scheduler schedule — 2025 and older) ──────────────────

export interface LegacyMatchPlayer {
  player_id?: number | null;
  first_name: string;
  last_name: string;
  gross_score: number;
  net_score: number;
  is_disqualified?: boolean;
  is_guest?: boolean;
}

export interface LegacyMatch {
  round_id: number;
  players: LegacyMatchPlayer[];
}

export interface LegacyRow {
  name: string;
  player_id: number | null;
  scores: (number | null)[];
  total: number;
  dq: boolean;
  guest: boolean;
  completed: boolean;
  /** 0 for anyone not ranked (DQ, DNF, guest). */
  rank: number;
}

export interface LegacyStandings {
  /** Ranked finishers, then DQ, then DNF, then guests. */
  rows: LegacyRow[];
  ranked: LegacyRow[];
  countedRounds: number;
}

/**
 * Matches vs Rounds: every match row is one physical session, but a major can
 * run parallel/split sessions, so a player's progress is measured in ROUNDS —
 * their matches folded into play order (R1, R2, …). Matches must already be in
 * play order (the API returns them date ASC). Names identify players because
 * older rows may carry no player_id.
 *
 * Counted rounds = what the major is scored over: the points engine's value
 * when it has one (so the leaderboard, points and titles agree), otherwise the
 * most common number of competition matches.
 */
export function buildLegacyStandings(matches: LegacyMatch[], countedFromResults: number, metric: Metric): LegacyStandings {
  const acc = new Map<string, {
    player_id: number | null;
    comp: (number | null)[];        // competition (non-guest) scores, in play order
    guestScores: (number | null)[]; // scores played as a non-competing guest
    dq: boolean;
    guest: boolean;
  }>();
  matches.forEach((match) => {
    match.players.forEach((p) => {
      const name = `${p.first_name} ${p.last_name}`;
      let rec = acc.get(name);
      if (!rec) { rec = { player_id: p.player_id ?? null, comp: [], guestScores: [], dq: false, guest: false }; acc.set(name, rec); }
      const val = metric === 'gross' ? p.gross_score : p.net_score;
      if (p.is_guest) { rec.guest = true; rec.guestScores.push(val); }
      else { rec.comp.push(val); }
      if (p.is_disqualified) rec.dq = true; // a DQ in any match = DQ'd from the event
    });
  });

  const modeRounds = (() => {
    const freq = new Map<number, number>();
    Array.from(acc.values()).filter((r) => r.comp.length > 0).forEach((r) => freq.set(r.comp.length, (freq.get(r.comp.length) ?? 0) + 1));
    let best = 0, bestFreq = 0;
    freq.forEach((f, c) => { if (f > bestFreq || (f === bestFreq && c > best)) { bestFreq = f; best = c; } });
    return best;
  })();
  const countedRounds = countedFromResults > 0 ? countedFromResults : modeRounds;

  const allRows = Array.from(acc.entries()).map(([name, rec]) => {
    const pureGuest = rec.guest && rec.comp.length === 0; // played only as a guest
    const scores = pureGuest ? rec.guestScores : rec.comp;
    return {
      name,
      player_id: rec.player_id,
      scores,
      total: scores.reduce<number>((a, v) => a + (v ?? 0), 0),
      dq: rec.dq,
      guest: pureGuest,
      completed: !pureGuest && countedRounds > 0 && rec.comp.length >= countedRounds,
    };
  }).filter((p) => p.scores.some((v) => v !== null));

  // Field, in order: finishers ranked by total, then DQ'd players (kept in the
  // field, last), then players who didn't finish (DNF), then guests. Only
  // finishers are ranked — mirroring the points engine, titles and finishes.
  const competitors = allRows.filter((p) => !p.guest);
  const ranked = withCompetitionRanks(competitors.filter((p) => !p.dq && p.completed).sort((a, b) => a.total - b.total));
  const dqRows = competitors.filter((p) => p.dq).sort((a, b) => a.total - b.total).map((p) => ({ ...p, rank: 0 }));
  const dnfRows = competitors.filter((p) => !p.dq && !p.completed).sort((a, b) => a.total - b.total).map((p) => ({ ...p, rank: 0 }));
  const guestRows = allRows.filter((p) => p.guest).sort((a, b) => a.total - b.total).map((p) => ({ ...p, rank: 0 }));
  return { rows: [...ranked, ...dqRows, ...dnfRows, ...guestRows], ranked, countedRounds };
}

// ── What the Analysis view needs: the finishing order ───────────────────────

export type StandingStatus = 'ranked' | 'dq' | 'dnf' | 'guest';

export interface Standing {
  player_id: number | null;
  name: string;
  /** Finishing place (ties share it); null when not ranked. */
  rank: number | null;
  status: StandingStatus;
}

export function standingsFromScheduled(rows: ScheduledRow[]): Standing[] {
  return rows.map((r) => ({ player_id: r.player_id, name: r.name, rank: r.rank, status: 'ranked' as const }));
}

export function standingsFromLegacy(rows: LegacyRow[]): Standing[] {
  return rows.map((r) => {
    const status: StandingStatus = r.guest ? 'guest' : r.dq ? 'dq' : !r.completed ? 'dnf' : 'ranked';
    return { player_id: r.player_id, name: r.name, rank: status === 'ranked' ? r.rank : null, status };
  });
}
